import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Search, Menu,  GraduationCap, Users, Trophy, ArrowRight } from 'lucide-react';

const OhioState: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Ohio State University";

    return (
        <div className="min-h-screen bg-white font-sans text-[#333] selection:bg-[#BB0000] selection:text-white">
            {/* Utility Bar - Gray */}
            <div className="bg-[#e7e7e7] text-[#333] py-1 px-6 flex justify-end gap-6 text-xs font-bold">
                 <a href="#" className="hover:underline">Help</a>
                 <a href="#" className="hover:underline">BuckeyeLink</a>
                 <a href="#" className="hover:underline">Map</a>
                 <a href="#" className="hover:underline">Find People</a>
                 <a href="#" className="hover:underline">Webmail</a>
            </div>

            {/* Nav */}
            <nav className="bg-white py-6 px-6 md:px-12 border-b border-gray-200">
                <div className="max-w-[1200px] mx-auto flex justify-between items-center">
                    <div className="flex items-center gap-2">
                        <div className="bg-[#BB0000] text-white w-10 h-10 flex items-center justify-center font-serif font-black text-2xl rounded-sm">O</div>
                         <div className="flex flex-col">
                            <span className="font-serif text-xl font-bold text-[#333] leading-none uppercase">{schoolName}</span>
                        </div>
                    </div>

                    <div className="hidden lg:flex gap-8 font-bold text-[#333] text-sm">
                        <a href="#" className="hover:text-[#BB0000] hover:underline underline-offset-4 decoration-2">Academics</a>
                        <a href="#" className="hover:text-[#BB0000] hover:underline underline-offset-4 decoration-2">Admissions</a>
                        <a href="#" className="hover:text-[#BB0000] hover:underline underline-offset-4 decoration-2">Research</a>
                        <a href="#" className="hover:text-[#BB0000] hover:underline underline-offset-4 decoration-2">Athletics</a>
                        <a href="#" className="hover:text-[#BB0000] hover:underline underline-offset-4 decoration-2">About</a>
                    </div>
                    
                    <div className="flex gap-4 items-center">
                         <div className="bg-[#BB0000] text-white p-2 rounded-sm cursor-pointer hover:bg-[#990000]">
                            <Search size={20} />
                         </div>
                         <Menu className="cursor-pointer lg:hidden" size={24} />
                    </div>
                </div>
            </nav>

            {/* Hero - The Oval */}
            <header className="relative h-[650px]">
                <img 
                    src="https://images.unsplash.com/photo-1548439499-4d375369b71e?q=80&w=2070&auto=format&fit=crop" 
                    alt="The Oval" 
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#BB0000]/90 via-transparent to-transparent"></div>

                <div className="absolute bottom-0 left-0 w-full p-12 md:p-20">
                     <div className="max-w-4xl mx-auto text-white">
                         <h1 className="text-5xl md:text-7xl font-bold mb-6">
                             Time and Change.
                         </h1>
                         <p className="text-xl md:text-2xl mb-8 font-medium">
                             We are creators, innovators, and problem solvers. We are Buckeyes.
                         </p>
                         <Button className="bg-white text-[#BB0000] hover:bg-gray-100 font-bold px-10 h-14 rounded-sm text-lg">
                             Apply Now
                         </Button>
                     </div>
                </div>
            </header>

            {/* Stats - Scarlet and Gray */}
            <section className="bg-[#f2f2f2] py-24 px-6 md:px-12">
                <div className="max-w-[1200px] mx-auto grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
                     <div className="p-8 bg-white border-t-8 border-[#BB0000] shadow-sm">
                         <GraduationCap size={48} className="text-[#333] mx-auto mb-6" />
                         <h3 className="text-4xl font-black text-[#BB0000] mb-2">200+</h3>
                         <p className="font-bold text-gray-600">Majors</p>
                     </div>
                      <div className="p-8 bg-white border-t-8 border-[#333] shadow-sm">
                         <Users size={48} className="text-[#333] mx-auto mb-6" />
                         <h3 className="text-4xl font-black text-[#BB0000] mb-2">60k+</h3>
                         <p className="font-bold text-gray-600">Students</p>
                     </div>
                      <div className="p-8 bg-white border-t-8 border-[#BB0000] shadow-sm">
                         <Trophy size={48} className="text-[#333] mx-auto mb-6" />
                         <h3 className="text-4xl font-black text-[#BB0000] mb-2">Top 20</h3>
                         <p className="font-bold text-gray-600">Public University</p>
                     </div>
                </div>
            </section>

             {/* News Blocks */}
             <section className="py-24 px-6 md:px-12">
                 <div className="max-w-[1200px] mx-auto">
                     <h2 className="text-3xl font-bold text-[#333] mb-12 flex items-center gap-4">
                         <span className="bg-[#BB0000] w-2 h-10 block"></span>
                         {schoolName} News
                     </h2>

                     <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                         {[
                             "Wexner Medical Center ranked among nation's best",
                             "Research team discovers new planet in distant solar system",
                             "Agricultural college receives grant for sustainable farming",
                             "Buckeyes win national championship in synchronized swimming"
                         ].map((title, i) => (
                             <div key={i} className="group cursor-pointer">
                                 <div className="h-48 bg-gray-200 mb-4 rounded-sm overflow-hidden">
                                     <img src={`https://source.unsplash.com/random/400x300?sig=${i+10}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" alt="News"/>
                                 </div>
                                 <h3 className="text-lg font-bold text-[#333] group-hover:text-[#BB0000] transition-colors mb-2 leading-snug">{title}</h3>
                                 <div className="flex items-center gap-1 text-[#BB0000] text-sm font-bold opacity-0 group-hover:opacity-100 transition-opacity">
                                     Read More <ArrowRight size={14}/>
                                 </div>
                             </div>
                         ))}
                     </div>
                 </div>
             </section>

             {/* Footer */}
             <footer className="bg-[#333] text-white py-16 px-6 md:px-12 border-t-8 border-[#BB0000]">
                  <div className="max-w-[1200px] mx-auto flex flex-col md:flex-row justify-between gap-12 font-sans text-sm">
                      <div className="max-w-xs">
                          <div className="flex items-center gap-2 mb-6">
                            <div className="bg-[#BB0000] text-white w-8 h-8 flex items-center justify-center font-serif font-black text-xl rounded-sm">O</div>
                            <span className="font-serif font-bold text-lg uppercase">The {schoolName}</span>
                          </div>
                          <p className="text-gray-400 mb-6">
                              Columbus, OH 43210 <br/>
                              614-292-6446
                          </p>
                      </div>

                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 font-bold text-gray-400">
                          <ul className="space-y-3 hover:[&_a]:text-[#BB0000]">
                              <li><a href="#">Diversity</a></li>
                              <li><a href="#">Jobs</a></li>
                              <li><a href="#">Accessibility</a></li>
                              <li><a href="#">Nondiscrimination</a></li>
                          </ul>
                          <ul className="space-y-3 hover:[&_a]:text-[#BB0000]">
                              <li><a href="#">Privacy</a></li>
                              <li><a href="#">Title IX</a></li>
                              <li><a href="#">Contact</a></li>
                          </ul>
                      </div>
                  </div>
                  <div className="max-w-[1200px] mx-auto mt-16 text-center text-xs text-gray-500">
                      © 2026 {schoolName}
                  </div>
             </footer>
        </div>
    );
};

export default OhioState;
